// US Map Component for InstantSearch Integration
import { BaseMapUtil } from './baseMapUtil.js';

export default function createUsMapComponent(instanceId = '') {
	return {
		template: instanceId ? '#us-map-template-' + instanceId : '#us-map-template',
		props: ['refinementItems', 'refineFunction'],
		emits: ['state-selected'],
		data() {
			return {
				usStatesData: null,
				statesSelection: null,
				selectedStates: [],
				svgSelection: null,
				zoomBehavior: null,
				pathGenerator: null,
				isZooming: false,
				zoomTimeout: null,
				instanceId: instanceId
			}
		},
		mounted() {
			this.initializeMap();
		},
		watch: {
			refinementItems: {
				handler(newItems) {
					// Update map when refinement items change (dropdown selection)
					// console.log('Refinement items changed:', newItems)
					
					// Clear any existing zoom timeout to prevent multiple zooms
					if (this.zoomTimeout) {
						clearTimeout(this.zoomTimeout);
						this.zoomTimeout = null;
					}
					
					if (newItems && newItems.length > 0) {
						// Get all refined state names
						const selectedStateNames = newItems
							.filter(item => item.isRefined)
							.map(item => item.value);
						
						if (selectedStateNames.length > 0) {
							this.selectStatesByNames(selectedStateNames);
						} else {
							this.clearSelection();
						}
					} else {
						this.clearSelection();
					}
				},
				deep: true
			}
		},
		methods: {
			async initializeMap() {
				const mapContext = await BaseMapUtil.initializeBaseMap({
					containerId: this.instanceId ? 'map-container-' + this.instanceId : 'map-container',
					svgId: this.instanceId ? 'us-map-svg-' + this.instanceId : 'us-map-svg',
					errorPrefix: 'Map',
					enableZoom: true,
					onStateClick: (event, d) => {
						this.onStateClick(d, mapContext.pathGenerator, mapContext.zoom, mapContext.svgSelection, mapContext.statesSelection);
					},
					getStateColor: () => 'rgba(31, 52, 90, 0.2)',
					afterStatesDrawn: ({ svgSelection, zoom, states, width }) => {
						this.addZoomControls(svgSelection, zoom, states, width);
					}
				});

				if (mapContext) {
					// Store references
					this.svgSelection = mapContext.svgSelection;
					this.zoomBehavior = mapContext.zoom;
					this.pathGenerator = mapContext.pathGenerator;
					this.statesSelection = mapContext.statesSelection;
					this.stateLabels = mapContext.stateLabels;
					this.usStatesData = mapContext.usStatesData;
				}
			},
			
			onStateClick(stateData, path, zoom, svg, states) {
				// Update refinement using InstantSearch (toggle state selection)
				this.refineFunction(stateData.properties.name);
				
				// Update visual selection will be handled by the watcher when refinementItems change
				// No need to manually update colors here as InstantSearch will trigger the watcher
			},
			
			selectStatesByNames(stateNames) {
				if (!this.usStatesData || !this.statesSelection || !stateNames || stateNames.length === 0) {
					return;
				}
				
				// Find all state features that match the names
				const stateFeatures = this.usStatesData.features.filter(feature => 
					stateNames.includes(feature.properties.name)
				);
				
				if (stateFeatures.length > 0 && this.statesSelection) {
					// Update state colors - highlight all selected states
					this.statesSelection.attr('fill', (d) => 
						stateNames.includes(d.properties.name) ? '#1F345A' : 'rgba(31, 52, 90, 0.2)'
					);
					
					// Update state label text colors
					if (this.stateLabels) {
						this.stateLabels.attr('fill', (d) => 
							stateNames.includes(d.properties.name) ? 'white' : '#333333'
						);
					}
					
					// Store and emit selected states
					this.selectedStates = stateFeatures;
					this.$emit('state-selected', stateFeatures);
					
					// Clear existing zoom timeout if any
					if (this.zoomTimeout) {
						clearTimeout(this.zoomTimeout);
					}
					
					// Debounced zoom - only zoom once even if called multiple times
					this.zoomTimeout = setTimeout(() => {
						if (!this.isZooming) {
							this.isZooming = true;
							this.zoomToStates(stateFeatures);
						}
						this.zoomTimeout = null;
					}, 350); // Wait for CSS transition + buffer
				}
			},
			
			selectStateByName(stateName) {
				this.selectStatesByNames([stateName]);
			},
			
			clearSelection() {
				if (this.statesSelection) {
					this.statesSelection.attr('fill', 'rgba(31, 52, 90, 0.2)');
				}
				
				// Reset state label text colors to default
				if (this.stateLabels) {
					this.stateLabels.attr('fill', '#333333');
				}
				
				this.selectedStates = [];
				this.$emit('state-selected', null);
				
				// Reset the isZooming flag to allow future zooms
				this.isZooming = false;
				
				// Reset zoom to show full map (initial fitted view)
				if (this.svgSelection && this.zoomBehavior && this.usStatesData) {
					// Get current container dimensions
					const containerId = this.instanceId ? 'map-container-' + this.instanceId : 'map-container';
					const container = document.getElementById(containerId);
					if (container) {
						const containerRect = container.getBoundingClientRect();
						const width = containerRect.width;
						const height = containerRect.height;
						
						// Move reset button back to the right side
						d3.select('.zoom-controls-right').attr('transform', `translate(${width-100}, 20)`);
						
						// Calculate the initial fitted bounds for all states (tighter fit like original)
						const bounds = this.pathGenerator.bounds(this.usStatesData);
						this.zoomToInitialView(bounds, width, height);
					} else {
						// Fallback to identity transform
						this.svgSelection.transition().duration(750).call(
							this.zoomBehavior.transform,
							d3.zoomIdentity
						);
					}
				}
			},
			
			zoomToInitialView(bounds, width, height) {
				const [[x0, y0], [x1, y1]] = bounds;
				const dx = x1 - x0;
				const dy = y1 - y0;
				const x = (x0 + x1) / 2;
				const y = (y0 + y1) / 2;
				
				// Calculate scale with minimal padding (like fitSize)
				const scale = Math.min(8, 0.95 / Math.max(dx / width, dy / height));
				const translate = [width / 2 - scale * x, height / 2 - scale * y];
				
				this.svgSelection.transition().duration(750)
					.call(
						this.zoomBehavior.transform,
						d3.zoomIdentity.translate(translate[0], translate[1]).scale(scale)
					)
					.on('end', () => {
						// Reset zooming flag when animation completes
						this.isZooming = false;
					});
			},
			
			zoomToStates(stateFeatures) {
				if (!this.svgSelection || !this.zoomBehavior || !this.pathGenerator || !stateFeatures || stateFeatures.length === 0) {
					return;
				}
				
				// Get current container dimensions (accounts for layout changes)
				const containerId = this.instanceId ? 'map-container-' + this.instanceId : 'map-container';
				const container = document.getElementById(containerId);
				if (!container) return;
				
				const containerRect = container.getBoundingClientRect();
				const width = containerRect.width;
				const height = containerRect.height;
				// Update SVG dimensions to match current container
				this.svgSelection
					.attr('width', '100%')
					.attr('height', height);

					
				// Move zoom controls to match current width scale
				d3.select('.zoom-controls-right').attr('transform', `translate(${width-100}, 20)`);
				
				if (stateFeatures.length === 1) {
					// Single state - zoom to fit the state
					const bounds = this.pathGenerator.bounds(stateFeatures[0]);
					this.zoomToBounds(bounds, width, height);
				} else {
					// Multiple states - calculate combined bounding box
					const combinedBounds = this.calculateCombinedBounds(stateFeatures);
					if (combinedBounds) {
						this.zoomToBounds(combinedBounds, width, height);
					}
				}
			},
			
			calculateCombinedBounds(stateFeatures) {
				if (!stateFeatures || stateFeatures.length === 0) return null;
				
				let minX = Infinity, minY = Infinity;
				let maxX = -Infinity, maxY = -Infinity;
				
				stateFeatures.forEach(feature => {
					const bounds = this.pathGenerator.bounds(feature);
					const [[x0, y0], [x1, y1]] = bounds;
					
					minX = Math.min(minX, x0);
					minY = Math.min(minY, y0);
					maxX = Math.max(maxX, x1);
					maxY = Math.max(maxY, y1);
				});
				
				return [[minX, minY], [maxX, maxY]];
			},
			
			zoomToBounds(bounds, width, height) {
				const [[x0, y0], [x1, y1]] = bounds;
				const dx = x1 - x0;
				const dy = y1 - y0;
				const x = (x0 + x1) / 2;
				const y = (y0 + y1) / 2;
				
				// Calculate scale with padding
				const scale = Math.min(8, 0.8 / Math.max(dx / width, dy / height));
				const translate = [width / 2 - scale * x, height / 2 - scale * y];
				
				this.svgSelection.transition().duration(750)
					.call(
						this.zoomBehavior.transform,
						d3.zoomIdentity.translate(translate[0], translate[1]).scale(scale)
					)
					.on('end', () => {
						// Reset zooming flag when animation completes
						this.isZooming = false;
						
						// Reapply selected state colors after zoom completes
						this.reapplyStateSelection();
					});
			},
			
			reapplyStateSelection() {
				// Reapply state selection colors if we have selected states
				if (this.selectedStates && this.selectedStates.length > 0 && this.statesSelection) {
					const selectedStateNames = this.selectedStates.map(state => state.properties.name);
					
					// Update state fill colors
					this.statesSelection.attr('fill', (d) => 
						selectedStateNames.includes(d.properties.name) ? '#1F345A' : 'rgba(31, 52, 90, 0.2)'
					);
					
					// Update state label text colors
					if (this.stateLabels) {
						this.stateLabels.attr('fill', (d) => 
							selectedStateNames.includes(d.properties.name) ? 'white' : '#333333'
						);
					}
				}
			},
			
			addZoomControls(svgSelection, zoom, states, width) {
				// Add zoom controls on the left side
				const leftControlsContainer = svgSelection.append('g')
					.attr('class', 'zoom-controls-left')
					.attr('transform', 'translate(20, 20)');

				// Zoom In button
				const zoomInGroup = leftControlsContainer.append('g')
					.attr('class', 'zoom-in-control');
				
				zoomInGroup.append('rect')
					.attr('width', 40)
					.attr('height', 40)
					.attr('fill', '#1F345A')
					.attr('stroke', '#ffffff')
					.attr('rx', 4)
					.style('cursor', 'pointer')
					.on('click', () => {
						const component = this;
						svgSelection.transition().duration(300)
							.call(zoom.scaleBy, 1.5)
							.on('end', () => {
								component.reapplyStateSelection();
							});
					});

				zoomInGroup.append('text')
					.attr('x', 20)
					.attr('y', 26)
					.attr('text-anchor', 'middle')
					.attr('fill', 'white')
					.attr('font-size', '18px')
					.attr('font-weight', 'bold')
					.style('pointer-events', 'none')
					.text('+');

				// Zoom Out button
				const zoomOutGroup = leftControlsContainer.append('g')
					.attr('class', 'zoom-out-control')
					.attr('transform', 'translate(0, 50)');
				
				zoomOutGroup.append('rect')
					.attr('width', 40)
					.attr('height', 40)
					.attr('fill', '#1F345A')
					.attr('stroke', '#ffffff')
					.attr('rx', 4)
					.style('cursor', 'pointer')
					.on('click', () => {
						const component = this;
						svgSelection.transition().duration(300)
							.call(zoom.scaleBy, 1 / 1.5)
							.on('end', () => {
								component.reapplyStateSelection();
							});
					});

				zoomOutGroup.append('text')
					.attr('x', 20)
					.attr('y', 26)
					.attr('text-anchor', 'middle')
					.attr('fill', 'white')
					.attr('font-size', '18px')
					.attr('font-weight', 'bold')
					.style('pointer-events', 'none')
					.text('−');

				// Reset button on the right side
				const rightControlsContainer = svgSelection.append('g')
					.attr('class', 'zoom-controls-right')
					.attr('transform', `translate(${width - 100}, 20)`);

				const resetGroup = rightControlsContainer.append('g')
					.attr('class', 'reset-zoom-control');
				
				resetGroup.append('rect')
					.attr('width', 80)
					.attr('height', 30)
					.attr('fill', '#1F345A')
					.attr('stroke', '#ffffff')
					.attr('rx', 4)
					.style('cursor', 'pointer')
					.on('click', () => {
						this.clearSelection();
						if (this.refineFunction) {
							// Clear all refinements
							if (this.refinementItems) {
								this.refinementItems.forEach(item => {
									if (item.isRefined) {
										this.refineFunction(item.value);
									}
								});
							}
						}
						d3.select('.zoom-controls-right').attr('transform', `translate(${width-100}, 20)`);

						svgSelection.transition().duration(750).call(zoom.transform, d3.zoomIdentity);
					});

				resetGroup.append('text')
					.attr('x', 40)
					.attr('y', 20)
					.attr('text-anchor', 'middle')
					.attr('fill', 'white')
					.attr('font-size', '12px')
					.style('pointer-events', 'none')
					.text('Reset');
			}
		}
	};
}